import React from 'react'
import { To } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'

import { useDataTable } from 'modules/core/components/DataTable'
import Popover from 'modules/core/components/PopoverMenu'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DeleteForeverModal from 'modules/documents/components/TrashBin/TrashBinDataTable/_bulk_actions/DeleteForeverModal'
import RestoreModal from 'modules/documents/components/TrashBin/TrashBinDataTable/_bulk_actions/RestoreModal'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import useRemoveFromTrashBinDocument from 'modules/documents/hooks/documents/useRemoveFromTrashBinDocument'
import useRestoreFromTrashBinDocument from 'modules/documents/hooks/documents/useRestoreFromTrashBinDocument'
import { canDeleteDocument, canUpdateDocument } from 'modules/documents/lib/Documents'
import { DocumentsDataTableQuery } from 'generated/resources/datatable.graphql'

interface Props {
  document: Document
  getItemRoute: (documentId: string) => To
  deleteFeatureEnabled: boolean
}

const Actions = ({ document, getItemRoute, deleteFeatureEnabled }: Props) => {
  const [restoreFilesModalOpen, setRestoreFilesModalOpen] = React.useState(false)
  const [deleteFilesForeverModalOpen, setDeleteFilesForeverModalOpen] = React.useState(false)
  const { selectedRows } = useDataTable<DocumentsDataTableQuery>()
  const { restoreDocumentsFromTrashBin, loading: loadingRestore } = useRestoreFromTrashBinDocument({
    fromDataTable: true,
  })
  const { removeDocumentsFromTrashBin, loading: loadingRemove } = useRemoveFromTrashBinDocument({
    fromDataTable: true,
  })
  const selectedIds = useUnsafeMemo(() => {
    return [...selectedRows]
  }, [selectedRows])

  const onSubmitRestore = async () => {
    const result = await restoreDocumentsFromTrashBin({ documentIds: [document.id] })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.tp('documents.trash_bin.restore.success', { count: 1 }),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }
  }

  const onSubmitDeleteForever = async () => {
    const result = await removeDocumentsFromTrashBin({ documentIds: [document.id] })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.tp('documents.trash_bin.delete_forever.success', { count: 1 }),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }
  }

  if (!document.id) return null

  const canDelete = deleteFeatureEnabled ? canDeleteDocument(document) : canUpdateDocument(document)

  return (
    <>
      <Box gap="s4" flexDirection="row" justifyContent="flexEnd">
        {canDelete && (
          <Popover type="list">
            <Popover.Trigger disabled={selectedIds.includes(document.id)}>
              <Button.Decorative
                type="secondary"
                iconName="ellipsis"
                disabled={selectedIds.includes(document.id)}
              />
            </Popover.Trigger>
            <Popover.Content side="bottom" align="end">
              <Popover.TouchableOption
                icon="history"
                onClick={() => setRestoreFilesModalOpen(true)}
                title={i18n.t('documents.trash_bin.restore_file')}
              />
              <Popover.TouchableOption
                icon="delete"
                danger
                onClick={() => setDeleteFilesForeverModalOpen(true)}
                title={i18n.t('documents.trash_bin.delete')}
              />
            </Popover.Content>
          </Popover>
        )}
        <Button.Router
          type="secondary"
          aria-label={i18n.t('common.view_document')}
          to={getItemRoute(document.id)}
          iconName="arrow-right"
        />
      </Box>
      {restoreFilesModalOpen && (
        <RestoreModal
          filename={document.filename}
          totalDocuments={1}
          loading={loadingRestore}
          onSubmit={onSubmitRestore}
          onClose={() => setRestoreFilesModalOpen(false)}
        />
      )}

      {deleteFilesForeverModalOpen && (
        <DeleteForeverModal
          filename={document.filename}
          totalDocuments={1}
          onClose={() => setDeleteFilesForeverModalOpen(false)}
          onSubmit={onSubmitDeleteForever}
          loading={loadingRemove}
        />
      )}
    </>
  )
}

export default Actions
